import * as Yup from "yup";

// Valores iniciales del formulario
export const isoInitialValues = {
  isoName: "",
  iso: {
    email: "",
    currencyCode: "",
    address: "",
    neighborhood: "",
    city: "",
    county: "",
    state: "",
    postalCode: "",
    countryCode: "",
    amountTxProcessing: "",
    amountTxProcessingTiers: "",
    percentTxProcessing: "",
    percentTxProcessingTiers: "",
    amountTerminalMaintenance: "0",
    amountTerminalMaintenanceTiers: "",
    terminalMaintenanceInactive: "",
    amountTerminalSetup: "",
    amountTerminalSetupTiers: "",
    effectiveDate: "",
  },
  bank: {
    Iban: "",
    address: "",
    accountNumber: "",
    routingNumber: "",
  },
  crypto_wallets: [],
};

// Schema de validación con Yup
export const isoValidationSchema = (isEditMode = false) => {
  return Yup.object().shape({
    isoName: Yup.string()
      .required("Business Name is required")
      .max(16, "Business Name must be at most 16 characters"),
    iso: Yup.object().shape({
      email: isEditMode
        ? Yup.string().email("Invalid email format")
        : Yup.string()
            .required("Email is required")
            .email("Invalid email format")
            .max(32, "Email must be at most 32 characters"),
      currencyCode: Yup.string().required("Currency is required"),
      address: Yup.string()
        .required("Address is required")
        .min(5, "Address must be at least 5 characters")
        .max(32, "Address cannot exceed 32 characters"),
      neighborhood: Yup.string().max(
        32,
        "Neighborhood must be at most 32 characters"
      ),
      city: Yup.string()
        .required("City is required")
        .min(2, "City must be at least 2 characters")
        .max(32, "City cannot exceed 32 characters"),
      county: Yup.string().max(16, "County must be at most 16 characters"),
      state: Yup.string()
        .required("State is required")
        .min(2, "State must be at least 2 characters")
        .max(30, "State cannot exceed 30 characters"),
      postalCode: Yup.string()
        .required("Postal Code is required")
        .min(3, "Postal code must be at least 3 characters")
        .max(16, "Postal code cannot exceed 16 characters"),
      countryCode: Yup.string()
        .required("Country Code is required")
        .max(2, "Country Code must be 2 characters"),
      amountTxProcessing: Yup.number()
        .typeError("Transaction processing amount must be a number")
        .required("Transaction processing amount is required")
        .min(0, "Transaction processing amount must be 0 or greater")
        .test(
          "decimal-places",
          "Amount must be an integer or have at most 2 decimal places (e.g., 0, 0.00, 1, 1.0, 1.00, 0.10)",
          (value) => {
            if (value === null || value === undefined) return false;

            const valueStr = value.toString();

            // Si no hay punto decimal → es entero → válido
            if (!valueStr.includes(".")) {
              return true;
            }

            // Si hay punto decimal → debe tener máximo 2 decimales
            const decimalPlaces = valueStr.split(".")[1];
            const isValid = decimalPlaces && decimalPlaces.length <= 2;

            return isValid;
          }
        ),
      amountTxProcessingTiers: Yup.string().max(
        16,
        "Amount TX Processing Tiers must be at most 16 characters"
      ),
      percentTxProcessing: Yup.number()
        .typeError("Transaction processing percentage must be a number")
        .required("Transaction processing percentage is required")
        .min(0, "Transaction processing percentage must be 0 or greater")
        .test(
          "decimal-places",
          "Percentage must be an integer or have at most 4 decimal places (e.g., 0, 0.0, 0.00, 20, 20.0, 20.00, 20.000, 20.0000, 0.5000)",
          (value) => {
            if (value === null || value === undefined) return false;

            const valueStr = value.toString();

            // Si no hay punto decimal → es entero → válido
            if (!valueStr.includes(".")) {
              return true;
            }

            // Si hay punto decimal → debe tener máximo 4 decimales
            const decimalPlaces = valueStr.split(".")[1];
            const isValid = decimalPlaces && decimalPlaces.length <= 4;

            return isValid;
          }
        ),
      percentTxProcessingTiers: Yup.string().max(
        6,
        "Percent TX Processing Tiers must be at most 6 characters"
      ),
      amountTerminalMaintenance: Yup.string().max(
        16,
        "Amount Terminal Maintenance must be at most 16 characters"
      ),
      amountTerminalMaintenanceTiers: Yup.string().max(
        16,
        "Amount Terminal Maintenance Tiers must be at most 16 characters"
      ),
      terminalMaintenanceInactive: Yup.string(),
      amountTerminalSetup: Yup.string().max(
        16,
        "Amount Terminal Setup must be at most 16 characters"
      ),
      amountTerminalSetupTiers: Yup.string().max(
        16,
        "Amount Terminal Setup Tiers must be at most 16 characters"
      ),
      effectiveDate: Yup.string(),
    }),
    bank: Yup.object().shape({
      Iban: Yup.string()
        .required("IBAN is required")
        .min(4, "IBAN must be at least 4 characters")
        .max(40, "IBAN cannot exceed 40 characters"),
      address: Yup.string()
        .required("Bank address is required")
        .min(5, "Address must be at least 5 characters")
        .max(32, "Address cannot exceed 32 characters"),
      accountNumber: Yup.string()
        .required("Account number is required")
        .min(5, "Account number must be at least 5 characters")
        .max(18, "Account number cannot exceed 18 characters"),
      routingNumber: Yup.string()
        .required("Routing number is required")
        // .length(9, "Routing number must be exactly 9 digits")
        // .matches(/^\d{9}$/, "Routing number must contain only digits"),
        .max(12, "Routing number cannot exceed 12 characters"),
    }),
    crypto_wallets: Yup.array(),
  });
};

// Función para setear valores del ISO en el formulario (para edición)
export const setIsoValues = ({ isoData, setFieldValue }) => {
  if (!isoData) return;

  // ISO Name (nivel raíz)
  if (isoData.isoName) {
    setFieldValue("isoName", isoData.isoName);
  }

  // ISO fields
  if (isoData.iso) {
    Object.keys(isoData.iso).forEach((key) => {
      if (isoData.iso[key] !== undefined && isoData.iso[key] !== null) {
        setFieldValue(`iso.${key}`, isoData.iso[key]);
      }
    });
  }

  // Currency Code (viene del nivel raíz pero va a iso.currencyCode)
  if (isoData.isoCurrencyCode) {
    setFieldValue("iso.currencyCode", isoData.isoCurrencyCode);
  }

  // Bank fields - mapear desde formato backend a formato formulario
  if (isoData.bank) {
    const bankMapping = {
      Iban: "Iban",
      address: "address",
      accountNumber: "accountNumber",
      routingNumber: "routingNumber",
    };

    Object.keys(bankMapping).forEach((backendKey) => {
      const formKey = bankMapping[backendKey];
      if (
        isoData.bank[backendKey] !== undefined &&
        isoData.bank[backendKey] !== null
      ) {
        setFieldValue(`bank.${formKey}`, isoData.bank[backendKey]);
      }
    });
  }

  // Crypto Wallets
  if (isoData.crypto_wallets) {
    setFieldValue("crypto_wallets", isoData.crypto_wallets);
  } else if (isoData.cryptoWallet) {
    setFieldValue("crypto_wallets", isoData.cryptoWallet);
  }
};

// Función para preparar datos para enviar al backend
export const prepareIsoDataForSubmit = (values, isoID = null) => {
  const data = {
    isoName: values.isoName,
    isoCurrencyCode: values.iso.currencyCode,
    iso: {
      address: values.iso.address,
      neighborhood: values.iso.neighborhood,
      city: values.iso.city,
      county: values.iso.county,
      state: values.iso.state,
      postalCode: values.iso.postalCode,
      countryCode: values.iso.countryCode,
      amountTxProcessing: values.iso.amountTxProcessing,
      amountTxProcessingTiers: values.iso.amountTxProcessingTiers,
      percentTxProcessing: values.iso.percentTxProcessing,
      percentTxProcessingTiers: values.iso.percentTxProcessingTiers,
      amountTerminalMaintenance: values.iso.amountTerminalMaintenance,
      amountTerminalMaintenanceTiers: values.iso.amountTerminalMaintenanceTiers,
      terminalMaintenanceInactive: values.iso.terminalMaintenanceInactive,
      amountTerminalSetup: values.iso.amountTerminalSetup,
      amountTerminalSetupTiers: values.iso.amountTerminalSetupTiers,
      effectiveDate: values.iso.effectiveDate,
    },
    bank: {
      Iban: values.bank.Iban,
      address: values.bank.address,
      accountNumber: values.bank.accountNumber,
      routingNumber: values.bank.routingNumber,
    },
    crypto_wallets: values.crypto_wallets || [],
  };

  // Si estamos editando, agregar el isoID
  if (isoID) {
    data.isoID = isoID;
  } else {
    // Si estamos creando, agregar el email
    data.iso.email = values.iso.email;
  }

  return data;
};
